import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Linkedin, CheckCircle2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const field = "mt-1.5 focus-visible:ring-emerald-brand";

export default function Contact() {
  const [content, setContent] = useState(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ company_name: "", contact_person: "", email: "", phone: "", hiring_requirement: "General Enquiry", positions: "-", message: "" });
  useEffect(() => { api.get("/content").then((r) => setContent(r.data)).catch(() => {}); }, []);
  const c = content?.contact || {};
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/hire-talent", { ...f, contact_person: f.contact_person, company_name: f.company_name || f.contact_person });
      setDone(true);
      toast.success("Message sent! We'll be in touch soon.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Failed to send"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <Header />
      <section className="bg-navy text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="font-display font-bold text-4xl tracking-tight">Contact Us</h1>
          <p className="mt-3 text-slate-300 max-w-2xl">Let's talk about your hiring needs or career goals. Reach out and our team will respond promptly.</p>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <h2 className="font-display font-semibold text-2xl text-navy">Get in touch</h2>
            <div className="space-y-4">
              {(c.phones || []).map((p) => (
                <div key={p} className="flex items-center gap-3"><span className="h-10 w-10 rounded-lg bg-emerald-brand/10 text-emerald-brand flex items-center justify-center"><Phone size={18} /></span><span className="text-navy font-medium">{p}</span></div>
              ))}
              <div className="flex items-center gap-3"><span className="h-10 w-10 rounded-lg bg-emerald-brand/10 text-emerald-brand flex items-center justify-center"><Mail size={18} /></span><span className="text-navy font-medium">{c.email}</span></div>
              <div className="flex items-center gap-3"><span className="h-10 w-10 rounded-lg bg-emerald-brand/10 text-emerald-brand flex items-center justify-center"><MapPin size={18} /></span><span className="text-navy font-medium">{c.address}</span></div>
              <a href={c.linkedin || "#"} target="_blank" rel="noreferrer" className="flex items-center gap-3"><span className="h-10 w-10 rounded-lg bg-emerald-brand/10 text-emerald-brand flex items-center justify-center"><Linkedin size={18} /></span><span className="text-navy font-medium">LinkedIn</span></a>
            </div>
          </div>
          {done ? (
            <div data-testid="contact-success" className="bg-white rounded-2xl border border-slate-200 p-10 text-center flex flex-col items-center justify-center">
              <CheckCircle2 className="text-emerald-brand" size={56} />
              <h2 className="mt-4 font-display font-semibold text-2xl text-navy">Message Sent!</h2>
              <p className="mt-2 text-slate-600">Thanks for reaching out. We'll get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-2xl border border-slate-200 p-8 space-y-5">
              <div><Label>Name *</Label><Input required data-testid="input-name" className={field} value={f.contact_person} onChange={on("contact_person")} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email *</Label><Input required type="email" data-testid="input-email" className={field} value={f.email} onChange={on("email")} /></div>
                <div><Label>Phone *</Label><Input required data-testid="input-phone" className={field} value={f.phone} onChange={on("phone")} /></div>
              </div>
              <div><Label>Company (optional)</Label><Input data-testid="input-company" className={field} value={f.company_name} onChange={on("company_name")} /></div>
              <div><Label>Message *</Label><Textarea required data-testid="input-message" className={field} rows={5} value={f.message} onChange={on("message")} /></div>
              <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-emerald-brand hover:bg-emerald-dark rounded-full py-6 text-base font-semibold">{loading ? "Sending…" : "Send Message"}</Button>
            </form>
          )}
        </div>
      </section>
      <Footer content={content} />
    </div>
  );
}
